// Prepares each mapped target: hides the publisher's own player, pins the
// slot's height, and keeps it hidden until the poster replaces it.
import { guardPlayIconStyles, setImportantStyle } from "@/mapping/playIcon";

export function hideLegacyPlayerUi(container: HTMLElement | null) {
  if (!container) {
    return;
  }

  setImportantStyle(container, "visibility", "visible");
  setImportantStyle(container, "opacity", "1");
  setImportantStyle(container, "overflow", "hidden");
  setImportantStyle(container, "z-index", "10");

  const computedPosition =
    typeof window.getComputedStyle === "function"
      ? window.getComputedStyle(container).position
      : "";
  if (!computedPosition || computedPosition === "static") {
    setImportantStyle(container, "position", "relative");
  }

  const hideSelectors = [
    ".sqs-video-icon",
    ".sqs-video-opaque",
    ".vjs-big-play-button",
    ".vjs-control-bar",
    ".videoWrapper",
    "video",
    "iframe",
    "picture",
    "button",
  ].join(", ");
  const legacyUi = container.querySelectorAll(hideSelectors);
  for (let i = 0; i < legacyUi.length; i++) {
    setImportantStyle(legacyUi[i], "display", "none");
    setImportantStyle(legacyUi[i], "visibility", "hidden");
    setImportantStyle(legacyUi[i], "opacity", "0");
    setImportantStyle(legacyUi[i], "pointer-events", "none");
  }

  // Some embeds (e.g. YouTube in GS/BF) live outside the overlay node.
  // Hide media elements in the nearest wrapper to prevent double players.
  // Look inward first (target may be an outer block wrapper such as
  // `.embed-block-wrapper`), then outward, then fall back to the container.
  const wrapper =
    container.querySelector(".sqs-video-wrapper") ||
    container.closest(".sqs-video-wrapper") ||
    container;
  if (!wrapper || wrapper === container) {
    return;
  }
  const externalMedia = wrapper.querySelectorAll("iframe, video");
  for (let i = 0; i < externalMedia.length; i++) {
    const media = externalMedia[i];
    const src = media.getAttribute("src") || "";
    if (src) {
      const cleanedSrc = src
        .replace(/([?&])autoplay=1(&|$)/i, "$1")
        .replace(/[?&]$/, "");
      if (cleanedSrc !== src) {
        media.setAttribute("src", cleanedSrc);
      }
    }
    if (
      media.tagName === "IFRAME" &&
      /src=|youtube\.com|youtu\.be/i.test(src)
    ) {
      try {
        (media as HTMLIFrameElement).contentWindow?.postMessage(
          '{"event":"command","func":"pauseVideo","args":""}',
          "https://www.youtube.com",
        );
      } catch (_e) {
        // ignore cross-origin postMessage errors
      }
    }
    setImportantStyle(media, "display", "none");
    setImportantStyle(media, "visibility", "hidden");
    setImportantStyle(media, "opacity", "0");
    setImportantStyle(media, "pointer-events", "none");
  }
}

export function forceShowPixmotoAnchor(anchor: HTMLElement | null) {
  if (!anchor) {
    return;
  }
  setImportantStyle(anchor, "display", "block");
  setImportantStyle(anchor, "width", "100%");
  setImportantStyle(anchor, "height", "100%");
  setImportantStyle(anchor, "max-width", "100%");
  setImportantStyle(anchor, "max-height", "100%");
  setImportantStyle(anchor, "visibility", "visible");
  setImportantStyle(anchor, "opacity", "1");
  setImportantStyle(anchor, "position", "absolute");
  setImportantStyle(anchor, "top", "0");
  setImportantStyle(anchor, "left", "0");
  setImportantStyle(anchor, "overflow", "hidden");
  setImportantStyle(anchor, "z-index", "20");

  const poster = anchor.querySelector(".pixmoto-mapped-poster");
  if (poster) {
    setImportantStyle(poster, "display", "block");
    setImportantStyle(poster, "visibility", "visible");
    setImportantStyle(poster, "opacity", "1");
    setImportantStyle(poster, "position", "absolute");
    setImportantStyle(poster, "top", "0");
    setImportantStyle(poster, "left", "0");
    setImportantStyle(poster, "width", "100%");
    setImportantStyle(poster, "max-width", "100%");
    setImportantStyle(poster, "height", "100%");
    setImportantStyle(poster, "max-height", "100%");
    setImportantStyle(poster, "object-fit", "cover");
    setImportantStyle(poster, "z-index", "21");
  }

  const mappedPlay = anchor.querySelector<HTMLElement>(
    ".pixmoto-mapped-play-icon",
  );
  if (mappedPlay) {
    // Stack above the poster.
    setImportantStyle(mappedPlay, "z-index", "22");
    // `opacity` is not in `PLAY_ICON_IMPORTANT_STYLES` because
    // popover.js writes `opacity = 1` after positioning to fade
    // the icon in — we want that behavior, so we just force it
    // visible up front and let popover's later writes pass
    // through the observer harmlessly (same value).
    setImportantStyle(mappedPlay, "opacity", "1");
    guardPlayIconStyles(mappedPlay);
  }
}

export function buildAnchorFill(): HTMLDivElement {
  // Padding-bottom aspect-ratio containers (e.g. `.embed-block-wrapper`)
  // have a content-box height of 0. An absolutely positioned fill div
  // resolves against the parent padding box, giving the anchor a real
  // pixel height regardless of whether the anchor itself ends up
  // `position: absolute` or `relative` after popover activation.
  const fill = document.createElement("div");
  fill.className = "pixmoto-anchor-fill";
  const styles: Record<string, string> = {
    position: "absolute",
    top: "0",
    left: "0",
    right: "0",
    bottom: "0",
    width: "100%",
    height: "100%",
    display: "block",
    "line-height": "0",
    overflow: "hidden",
    "z-index": "10",
  };
  for (const prop in styles) {
    setImportantStyle(fill, prop, styles[prop]);
  }
  return fill;
}

export function ensureAspectRatio(target: HTMLElement | null) {
  // The target wrapper (e.g. Squarespace `.intrinsic`) currently
  // derives its height from whatever child is inside it — an
  // `.embed-block-wrapper` with a padding-bottom aspect-ratio box,
  // or an `.image-inset` whose height comes from a constrained
  // `<img>`. Once we strip children and drop in an absolutely
  // positioned anchor fill, the wrapper would collapse. Capture
  // the wrapper's CURRENT rendered height and pin it so the new
  // poster occupies the exact same slot the original media did.
  //
  // Reading the rendered height (rather than intrinsic image
  // dimensions) is what makes this work across both layouts and
  // against host themes that cap the slot via max-height /
  // aspect-ratio / fluid-engine grid rules — we just inherit
  // whatever final size the layout already settled on.
  if (!target) {
    return;
  }
  const inlineStyle = target.getAttribute("style") || "";
  if (/padding-bottom\s*:/i.test(inlineStyle)) {
    return;
  }
  if (/aspect-ratio\s*:/i.test(inlineStyle)) {
    return;
  }

  const rect =
    typeof target.getBoundingClientRect === "function"
      ? target.getBoundingClientRect()
      : null;
  const renderedHeight = rect && rect.height > 0 ? rect.height : 0;
  if (renderedHeight > 0) {
    const px = `${Math.round(renderedHeight)}px`;
    setImportantStyle(target, "position", "relative");
    setImportantStyle(target, "height", px);
    setImportantStyle(target, "max-height", px);
    return;
  }

  // Fallback when the wrapper has zero rendered height at script
  // time (e.g. it's inside a hidden tab / lazy section). Look for
  // an inner aspect-ratio source: an inline `padding-bottom` on a
  // descendant (YouTube embed-block-wrapper) or intrinsic image
  // dimensions on a descendant `<img>`.
  const padDescendant = target.querySelector('[style*="padding-bottom"]');
  if (padDescendant) {
    const padMatch = (padDescendant.getAttribute("style") || "").match(
      /padding-bottom\s*:\s*([0-9.]+%)/i,
    );
    if (padMatch) {
      setImportantStyle(target, "position", "relative");
      setImportantStyle(target, "height", "0");
      setImportantStyle(target, "padding-bottom", padMatch[1]);
      return;
    }
  }

  const img = target.querySelector("img");
  if (!img) {
    return;
  }
  let width = 0;
  let height = 0;
  const dimsAttr = img.getAttribute("data-image-dimensions") || "";
  const dimsMatch = dimsAttr.match(/^(\d+)x(\d+)$/);
  if (dimsMatch) {
    width = Number.parseInt(dimsMatch[1], 10);
    height = Number.parseInt(dimsMatch[2], 10);
  }
  if (!width || !height) {
    width =
      Number.parseInt(img.getAttribute("width") || "0", 10) ||
      img.naturalWidth ||
      0;
    height =
      Number.parseInt(img.getAttribute("height") || "0", 10) ||
      img.naturalHeight ||
      0;
  }
  if (!width || !height) {
    return;
  }
  const ratio = (height / width) * 100;
  setImportantStyle(target, "position", "relative");
  setImportantStyle(target, "height", "0");
  setImportantStyle(target, "padding-bottom", `${ratio}%`);
}

export function setTargetsPending(
  targets: ArrayLike<HTMLElement>,
  isPending: boolean,
) {
  for (let i = 0; i < targets.length; i++) {
    const target = targets[i];
    if (isPending) {
      if (target.dataset.pixmotoPrevVisibility === undefined) {
        target.dataset.pixmotoPrevVisibility = target.style.visibility || "";
      }
      target.style.visibility = "hidden";
    } else {
      const prev = target.dataset.pixmotoPrevVisibility || "";
      target.style.visibility = prev || "visible";
      delete target.dataset.pixmotoPrevVisibility;
    }
  }
}

export function whenTargetsReady(
  targets: ArrayLike<HTMLElement>,
  callback: () => void,
) {
  // Squarespace classic image blocks ship `<img loading="lazy"
  // data-load="false">` and the surrounding `.intrinsic` wrapper
  // only reaches its final layout-settled height AFTER the image
  // loads (or after Squarespace's image-block JS swaps `data-src`
  // into `src`). Measuring `getBoundingClientRect()` at
  // DOMContentLoaded can return an intermediate placeholder height
  // (e.g. 276px instead of 368px), which in turn makes the
  // substituted poster slot collapse.
  //
  // Promote any inner images to eager loading so the browser
  // commits to fetching them, then wait for them all to settle
  // (load OR error). A safety timeout prevents lazy images that
  // never enter the viewport from blocking substitution forever.
  const imgs: HTMLImageElement[] = [];
  for (let i = 0; i < targets.length; i++) {
    const inner = targets[i].querySelectorAll("img");
    for (let j = 0; j < inner.length; j++) {
      imgs.push(inner[j]);
    }
  }

  const pending: HTMLImageElement[] = [];
  for (let i = 0; i < imgs.length; i++) {
    const img = imgs[i];
    try {
      img.loading = "eager";
    } catch (_e) {
      // `loading` is a writable property on HTMLImageElement in
      // modern browsers; ignore in older engines.
    }
    if (!img.complete) {
      pending.push(img);
    }
  }

  if (pending.length === 0) {
    callback();
    return;
  }

  let done = false;
  let remaining = pending.length;
  const finish = () => {
    if (done) {
      return;
    }
    done = true;
    callback();
  };
  const tick = () => {
    remaining -= 1;
    if (remaining <= 0) {
      finish();
    }
  };
  for (let i = 0; i < pending.length; i++) {
    pending[i].addEventListener("load", tick, { once: true });
    pending[i].addEventListener("error", tick, { once: true });
  }
  // Hard cap so a never-loading lazy image can't strand us.
  setTimeout(finish, 1500);
}
